import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/feature/SEOSchema";
import { getArticleBySlug, getRelatedArticles, type Article } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (!slug) return;
    const found = getArticleBySlug(slug);
    setArticle(found);
    if (found) {
      setRelatedArticles(getRelatedArticles(found.slug, found.categorySlug));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-20 bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl" />
            </div>
            <p className="text-foreground-500 text-lg mb-4">文章未找到</p>
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOSchema
        type="article"
        articleData={{
          title: article.title,
          slug: article.slug,
          excerpt: article.excerpt,
          author: article.author,
          date: article.date,
          coverImage: article.coverImage,
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-24 pb-20 bg-white">
        <article className="w-full max-w-4xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: article.category, path: `/category/${article.categorySlug}` },
              { name: article.title },
            ]}
          />

          <header className="mt-8 mb-8">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-50 text-foreground-400 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-5 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-time-line" />
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          <div className="aspect-[2/1] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - mogu`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
              prose-p:mb-4 prose-p:leading-relaxed
              prose-strong:text-foreground-950 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <aside className="mt-16 pt-10 border-t border-foreground-100">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[16/9] rounded-xl overflow-hidden mb-3">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        title={`${related.title} - mogu`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-foreground-700 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                  </Link>
                ))}
              </div>
            </aside>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}